// Fixed CreateSopForm.tsx
import React, { useMemo, useCallback } from "react";
import { Button } from "@shared/ui/button";
import { ChevronRight, Package, Settings, Wrench } from "lucide-react";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField } from "@shared/services/FormServices";
import {
  useServiceAssociationOptions,
  useUtilityServiceOptions,
} from "../../../hooks";

interface CreateSopFormProps {
  form: any; // react-hook-form form object
  onSubmit: (data: any) => boolean | void; // Made return type explicit
  onAssociationChange?: (value: string) => void;
  readOnly?: boolean;
  onNext?: () => void;
}

const CreateSopForm: React.FC<CreateSopFormProps> = ({
  form,
  onSubmit,
  onAssociationChange,
  readOnly = false,
  onNext,
}) => {
  // Fetch dynamic data
  const { options: associationOptions } = useServiceAssociationOptions();
  const { options: utilityOptions } = useUtilityServiceOptions();

  // Check if asset-related fields should be disabled
  const areAssetFieldsDisabled = form.watch("associatedWith") !== "2"; // "2" is the key for Asset

  const sopFormFields: FormField[] = useMemo(
    () => [
      {
        name: "name",
        label: "SOP Name",
        type: "text" as const,
        required: true,
        placeholder: "Enter SOP name...",
        helperText: "Name of the standard operating procedure",
        showHelperTooltip: true,
        tooltipText:
          "Provide a clear, descriptive name for this Standard Operating Procedure that will help users identify its purpose.",
        disabled: readOnly,
        className: "col-span-6",
        fullWidth: true,
      },
      {
        name: "description",
        label: "Description",
        type: "textarea" as const,
        required: true,
        placeholder: "Brief description of the SOP...",
        rows: 3,
        helperText: "Provide a brief overview of what this SOP covers",
        showHelperTooltip: true,
        tooltipText:
          "Describe the purpose and scope of this SOP. This helps users understand when and how to use this procedure.",
        disabled: readOnly,
        className: "col-span-6",
        fullWidth: true,
      },
      {
        name: "utilityType",
        label: "Utility Type",
        type: "select" as const,
        required: true,
        options: utilityOptions,
        placeholder: "Select utility type",
        helperText: "Choose which utility service this SOP applies to",
        showHelperTooltip: true,
        className: "col-span-3",
        tooltipText:
          "Select the specific utility type this SOP is designed for.",
        disabled: readOnly,
      },
      {
        name: "associatedWith",
        label: "Service Association",
        type: "select" as const,
        required: true,
        options: associationOptions,
        placeholder: "Select association",
        helperText: "Type of service this SOP is associated with",
        showHelperTooltip: true,
        className: "col-span-3",
        tooltipText:
          "Choose what type of service or entity this SOP is designed to work with. This determines which additional fields will be available.",
        disabled: readOnly,
        onChange: onAssociationChange,
      },
    ],
    [
      readOnly,
      onAssociationChange,
      areAssetFieldsDisabled,
      form.watch("associatedWith"),
      associationOptions,
      utilityOptions,
    ]
  );

  const handleFormSubmit = useCallback(
    (data: any) => {
      // Validate required fields manually if needed
      if (
        !data.name ||
        !data.description ||
        !data.utilityType ||
        !data.associatedWith
      ) {
        return;
      }

      // Call the provided onSubmit
      try {
        const result = onSubmit(data);

        // If onSubmit returns true or doesn't return anything, proceed with navigation
        if (result !== false && onNext) {
          onNext();
        }
      } catch (error) {}
    },
    [onSubmit, onNext]
  );

  const handleFieldChange = useCallback(
    (fieldName: string, value: any) => {
      // Handle any additional field change logic if needed
      if (fieldName === "associatedWith" && onAssociationChange) {
        onAssociationChange(value);
      }
    },
    [onAssociationChange]
  );

  const renderCustomFooter = useCallback(() => {
    if (readOnly) return null;

    return (
      <div className="pt-6 flex justify-end border-t border-gray-200">
        <Button
          type="submit"
          className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Continue <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    );
  }, [readOnly]);

  return (
    <div className="space-y-6 pt-4">
      {/* Unified Form with All Fields */}
      <DynamicForm
        fields={sopFormFields}
        form={form}
        onSubmit={handleFormSubmit}
        onFieldChange={handleFieldChange}
        config={{
          showSubmitButton: false,
          showResetButton: false,
          classes: {
            form: "space-y-6",
            button: "px-4 py-2 rounded-md font-medium transition-colors",
            buttonContainer: "flex justify-end gap-3 pt-6 border-gray-200 mt-8",
            input:
              "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
            textarea:
              "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical",
            select:
              "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white",
            label: "block text-sm font-medium text-gray-700 mb-1",
            error: "text-sm text-red-600 mt-1",
            container: "space-y-1",
          },
        }}
        gridColumns={6}
        responsiveColumns={{ sm: 1, md: 1 }}
        renderCustomFooter={renderCustomFooter}
        className="space-y-6"
      />

      {/* Conditional Asset Fields Notice */}
      {areAssetFieldsDisabled && (
        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-800">
            <strong>Note:</strong> Asset-specific fields will appear when
            "Asset" is selected as the service association.
          </p>
        </div>
      )}
    </div>
  );
};

export default CreateSopForm;
